import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
  Divider,
  Alert,
  Button,
} from '@mui/material';
import {
  Visibility,
  CheckCircle,
  Cancel,
  Print,
  FilterList,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { studentAPI, branchAPI, batchAPI, courseAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import ConfirmDialog from '../../../components/ConfirmDialog';

const PendingApprovals = () => {
  const { user, isSuperAdmin } = useAuth();

  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [total, setTotal] = useState(0);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [detailsDialog, setDetailsDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectionRemarks, setRejectionRemarks] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [branches, setBranches] = useState([]);
  const [batches, setBatches] = useState([]);
  const [courses, setCourses] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: null,
    studentId: null,
  });

  // Filters
  const [filters, setFilters] = useState({
    status: 'pending', // Always pending
    centre: '',
    search: '',
    batch: '',
    course: '',
    startDate: '',
    endDate: '',
  });

  useEffect(() => {
    fetchStudents();
    if (isSuperAdmin) {
      fetchBranches();
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchBatches();
    fetchCourses();
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll({ limit: 1000 });
      setBranches(response.data.data || []);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await batchAPI.getAll();
      setBatches(response.data.data?.batches || []);
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await courseAPI.getAll({ limit: 1000 });
      setCourses(response.data.data || []);
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  const fetchStudents = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: 'pending', // Force pending status
        ...filters,
      };

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      const response = await studentAPI.getAll(params);
      setStudents(response.data.data || []);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Error fetching students:', error);
      toast.error('Failed to load pending applications');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
    }));
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewDetails = async (studentId) => {
    try {
      const response = await studentAPI.getOne(studentId);
      setSelectedStudent(response.data.data.student);
      setDetailsDialog(true);
    } catch (error) {
      console.error('Error fetching student details:', error);
      toast.error('Failed to load student details');
    }
  };

  const handleApprove = (studentId) => {
    setConfirmDialog({
      open: true,
      title: 'Approve Application',
      message: 'Are you sure you want to approve this application?',
      studentId,
      onConfirm: async () => {
        try {
          setActionLoading(true);
          await studentAPI.approve(studentId, 'Application meets all requirements');
          toast.success('Application approved successfully');
          fetchStudents();
          setDetailsDialog(false);
        } catch (error) {
          console.error('Error approving application:', error);
          toast.error(error.response?.data?.message || 'Failed to approve application');
        } finally {
          setActionLoading(false);
        }
      },
    });
  };

  const handleRejectClick = (student) => {
    setSelectedStudent(student);
    setRejectionReason('');
    setRejectionRemarks('');
    setRejectDialog(true);
  };

  const handleRejectSubmit = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a rejection reason');
      return;
    }

    try {
      setActionLoading(true);
      await studentAPI.reject(selectedStudent._id, rejectionReason, rejectionRemarks);
      toast.success('Application rejected');
      fetchStudents();
      setRejectDialog(false);
      setDetailsDialog(false);
    } catch (error) {
      console.error('Error rejecting application:', error);
      toast.error(error.response?.data?.message || 'Failed to reject application');
    } finally {
      setActionLoading(false);
    }
  };

  const handlePrint = (student) => {
    sessionStorage.setItem('printStudent', JSON.stringify(student));
    const printWindow = window.open('/admin/students/print', '_blank');
    if (printWindow) {
      printWindow.focus();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Pending Approvals
        </Typography>
        <Chip label={`${total} Pending`} color="warning" />
      </Box>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Filters
          </Typography>
        </Box>
        <Grid container spacing={2}>
          {isSuperAdmin && (
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Centre</InputLabel>
                <Select
                  name="centre"
                  value={filters.centre}
                  onChange={handleFilterChange}
                  label="Centre"
                >
                  <MenuItem value="">All Centres</MenuItem>
                  {branches.map((branch) => (
                    <MenuItem key={branch._id} value={branch._id}>
                      {branch.name} ({branch.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              name="search"
              value={filters.search}
              onChange={handleFilterChange}
              placeholder="Name or Form Number"
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Batch</InputLabel>
              <Select
                name="batch"
                value={filters.batch}
                onChange={handleFilterChange}
                label="Batch"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Batches</MenuItem>
                {batches.map((b) => (
                  <MenuItem key={b._id} value={b._id}>
                    {b.name} ({b.year})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Course</InputLabel>
              <Select
                name="course"
                value={filters.course}
                onChange={handleFilterChange}
                label="Course"
                MenuProps={{ PaperProps: { style: { maxHeight: 320, overflowY: 'auto' } } }}
              >
                <MenuItem value="">All Courses</MenuItem>
                {courses.map((c) => (
                  <MenuItem key={c._id} value={c._id}>
                    {c.name} ({c.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              name="startDate"
              type="date"
              value={filters.startDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              name="endDate"
              type="date"
              value={filters.endDate}
              onChange={handleFilterChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper elevation={2}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Form No</strong></TableCell>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell><strong>Course</strong></TableCell>
                <TableCell><strong>Centre</strong></TableCell>
                <TableCell><strong>Submitted Date</strong></TableCell>
                <TableCell align="center"><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : students.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No pending applications found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                students.map((student) => (
                  <TableRow
                    key={student._id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => handleViewDetails(student._id)}
                  >
                    <TableCell>{student.formNumber || '-'}</TableCell>
                    <TableCell>{student.personalInfo?.nameAsPerTC || '-'}</TableCell>
                    <TableCell>
                      {student.course?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.course?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {student.centre?.name || '-'}
                      <br />
                      <Typography variant="caption" color="text.secondary">
                        {student.centre?.code || ''}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatDate(student.submittedAt)}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(student._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>

                      {isSuperAdmin && (
                        <>
                          <Tooltip title="Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleApprove(student._id)}
                            >
                              <CheckCircle />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleRejectClick(student)}
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}

                      <Tooltip title="Print">
                        <IconButton
                          size="small"
                          onClick={() => handlePrint(student)}
                        >
                          <Print />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[25, 50, 100]}
        />
      </Paper>

      {/* Details Dialog - Same as StudentList */}
      <Dialog
        open={detailsDialog}
        onClose={() => setDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedStudent && (
          <>
            <DialogTitle>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Student Application Details
                </Typography>
                <Chip label="PENDING" color="warning" size="small" />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3}>
                {selectedStudent.photo && (
                  <Grid item xs={12} md={3}>
                    <Card>
                      <CardMedia
                        component="img"
                        height="200"
                        image={`${process.env.REACT_APP_API_URL?.replace('/api', '')}/${selectedStudent.photo}`}
                        alt="Student Photo"
                      />
                    </Card>
                  </Grid>
                )}

                <Grid item xs={12} md={selectedStudent.photo ? 9 : 12}>
                  <Typography variant="subtitle2" color="text.secondary">Form Number</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>{selectedStudent.formNumber}</Typography>

                  <Typography variant="subtitle2" color="text.secondary">Course</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.course?.name} ({selectedStudent.course?.code})
                  </Typography>

                  <Typography variant="subtitle2" color="text.secondary">Centre</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {selectedStudent.centre?.name} ({selectedStudent.centre?.code})
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Personal Information</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Name (as per TC)</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.nameAsPerTC}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                  <Typography variant="body1">{formatDate(selectedStudent.personalInfo?.dateOfBirth)}</Typography>
                </Grid>
                <Grid item xs={12} md={3}>
                  <Typography variant="subtitle2" color="text.secondary">Gender</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.gender}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.phone}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedStudent.personalInfo?.email || '-'}</Typography>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              {isSuperAdmin && (
                <>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle />}
                    onClick={() => handleApprove(selectedStudent._id)}
                    disabled={actionLoading}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<Cancel />}
                    onClick={() => handleRejectClick(selectedStudent)}
                    disabled={actionLoading}
                  >
                    Reject
                  </Button>
                </>
              )}
              <Button onClick={() => setDetailsDialog(false)}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Application</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please provide a clear reason for rejection. The centre will be able to edit and resubmit the application.
          </Alert>
          <TextField
            label="Rejection Reason"
            fullWidth
            required
            multiline
            rows={3}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            label="Additional Remarks (Optional)"
            fullWidth
            multiline
            rows={2}
            value={rejectionRemarks}
            onChange={(e) => setRejectionRemarks(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={actionLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRejectSubmit}
            disabled={actionLoading || !rejectionReason.trim()}
          >
            {actionLoading ? 'Rejecting...' : 'Confirm Rejection'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        onConfirm={confirmDialog.onConfirm}
        title={confirmDialog.title}
        message={confirmDialog.message}
        confirmColor="success"
        confirmText="Approve"
      />
    </Box>
  );
};

export default PendingApprovals;
